import { Component, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { LocalizationService } from '../../core/localization/localization.service';
import { CustomerSelectorComponent } from '../../shared/design-system/customer-selector.component';
import { AinAmountPipe } from '../../shared/design-system/ain-amount.pipe';
import { AinDatePipe } from '../../shared/design-system/ain-date.pipe';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { SalesAiCopilotPanelComponent } from './sales-ai-copilot-panel.component';
import { REGULATED_PANEL_STYLES } from './sales-regulated-panel.styles';
import { RegulatedBrokerageOrderDetail, SalesRegulatedApiService } from './sales-regulated-api.service';

type BrokerTab = 'overview' | 'executions' | 'margin' | 'actions';

@Component({
  standalone: true,
  selector: 'ain-sales-brokerage-order-detail',
  imports: [
    AinPageComponent, AinStateComponent, RouterLink, FormsModule, ButtonModule,
    InputTextModule, InputNumberModule, SelectModule, TableModule,
    CustomerSelectorComponent, AinAmountPipe, AinDatePipe, SalesAiCopilotPanelComponent
  ],
  template: `
    <ain-page [title]="isCreate() ? t('sales.regulated.brokerage.newOrder') : t('sales.regulated.brokerage.detailTitle')" [description]="t('sales.regulated.brokerage.description')">
      @if (loading()) { <ain-state type="loading" [title]="t('common.loading')" /> } @else {
        <div class="layout">
          <section class="main">
            <header class="card"><a class="back-link" [routerLink]="['/sales/brokerage-orders']">{{ t('catalog.action.backToList') }}</a></header>
            @if (isCreate()) {
              <section class="card grid">
                <label><span>{{ t('sales.regulated.brokerage.fields.accountId') }}</span><input pInputText [(ngModel)]="form.accountId" /></label>
                <label><span>{{ t('sales.regulated.brokerage.fields.symbol') }}</span><input pInputText [(ngModel)]="form.symbol" /></label>
                <label><span>{{ t('sales.regulated.brokerage.fields.side') }}</span>
                  <p-select [options]="sides" optionLabel="value" optionValue="value" [(ngModel)]="form.side" appendTo="body" />
                </label>
                <label><span>{{ t('sales.regulated.brokerage.fields.quantity') }}</span><p-inputNumber [(ngModel)]="form.quantity" [min]="0" /></label>
                <label class="full"><span>{{ t('sales.orders.fields.customer') }}</span><ain-customer-selector [(ngModel)]="form.customerAccountId" /></label>
                <button pButton type="button" [label]="t('common.save')" (click)="save()"></button>
              </section>
            } @else if (detail(); as d) {
              <section class="summary card">
                <article><span>{{ t('sales.regulated.brokerage.fields.symbol') }}</span><strong>{{ d.symbol }}</strong></article>
                <article><span>{{ t('columns.status') }}</span><strong>{{ d.status }}</strong></article>
                <article><span>{{ t('sales.regulated.brokerage.fields.quantity') }}</span><strong>{{ d.quantity }}</strong></article>
              </section>
              <nav class="tabs">
                @for (tab of tabs; track tab) {
                  <button type="button" [class.active]="activeTab() === tab" (click)="activeTab.set(tab)">{{ tabLabel(tab) }}</button>
                }
              </nav>
              @if (activeTab() === 'executions') {
                <p-table [value]="d.executions">
                  <ng-template pTemplate="header"><tr><th>{{ t('sales.regulated.brokerage.fields.price') }}</th><th>{{ t('sales.regulated.brokerage.fields.quantity') }}</th><th>{{ t('columns.createdAt') }}</th></tr></ng-template>
                  <ng-template pTemplate="body" let-row><tr><td>{{ row.executedPrice | ainAmount }}</td><td>{{ row.executedQuantity }}</td><td>{{ row.executedAtUtc | ainDate:'datetime' }}</td></tr></ng-template>
                </p-table>
              }
              @if (activeTab() === 'margin') {
                <p-table [value]="d.marginSnapshots">
                  <ng-template pTemplate="header"><tr><th>{{ t('sales.regulated.brokerage.fields.equity') }}</th><th>{{ t('sales.regulated.brokerage.fields.usedMargin') }}</th><th>{{ t('sales.regulated.brokerage.fields.available') }}</th></tr></ng-template>
                  <ng-template pTemplate="body" let-row><tr><td>{{ row.equity | ainAmount }}</td><td>{{ row.usedMargin | ainAmount }}</td><td>{{ row.availableMargin | ainAmount }}</td></tr></ng-template>
                </p-table>
              }
              @if (activeTab() === 'actions') {
                <button pButton type="button" [label]="t('sales.regulated.brokerage.execute')" (click)="execute()"></button>
              }
            }
          </section>
          @if (!isCreate() && detail()) {
            <ain-sales-ai-copilot-panel entityType="BrokerageOrder" [entityId]="detail()!.id" />
          }
        </div>
      }
    </ain-page>
  `,
  styles: [REGULATED_PANEL_STYLES]
})
export class SalesBrokerageOrderDetailComponent implements OnInit {
  private readonly api = inject(SalesRegulatedApiService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localization = inject(LocalizationService);
  readonly loading = signal(true);
  readonly isCreate = signal(false);
  readonly detail = signal<RegulatedBrokerageOrderDetail | null>(null);
  readonly activeTab = signal<BrokerTab>('overview');
  readonly tabs: BrokerTab[] = ['overview', 'executions', 'margin', 'actions'];
  sides = [{ value: 'Buy' }, { value: 'Sell' }];
  form = { accountId: '', symbol: '', side: 'Buy', quantity: 1, customerAccountId: null as string | null };
  ngOnInit(): void {
    const id = this.route.snapshot.paramMap.get('orderId') ?? '';
    const accountId = this.route.snapshot.queryParamMap.get('accountId');
    this.isCreate.set(!id || id === 'new');
    if (accountId) this.form.accountId = accountId;
    if (this.isCreate()) { this.loading.set(false); return; }
    this.reload(id);
  }
  reload(id: string): void {
    this.api.getBrokerageOrder(id).subscribe({
      next: d => { this.detail.set(d); this.loading.set(false); },
      error: () => this.loading.set(false)
    });
  }
  save(): void {
    this.api.submitBrokerageOrder(this.form).subscribe({
      next: o => void this.router.navigate(['/sales/brokerage-orders', o.id])
    });
  }
  execute(): void {
    const id = this.route.snapshot.paramMap.get('orderId')!;
    if (!confirm(this.t('sales.regulated.confirm.execute'))) return;
    this.api.executeBrokerageOrder(id).subscribe({ next: () => this.reload(id) });
  }
  tabLabel(tab: BrokerTab): string {
    const map: Record<BrokerTab, string> = {
      overview: 'sales.regulated.tabs.overview',
      executions: 'sales.regulated.brokerage.tabs.executions',
      margin: 'sales.regulated.brokerage.tabs.margin',
      actions: 'sales.regulated.tabs.actions'
    };
    return this.t(map[tab]);
  }
  t(key: string): string { return this.localization.translate(key); }
}
